import * as breakpoints from './breakpoints';
import round from '../helpers/round';
import disableImageZoom from '../helpers/isIE11';
import typography from './typography';

export function remCalc(px) {
  if (Array.isArray(px)) {
    return px.map(remCalc);
  }
  return `${round(px / 16)}rem`;
}

export const expandAbsolutely = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

export const clearFix = {
  '&::before,&:after': {
    content: '" "',
    display: 'table',
  },
  '&:after': {
    clear: 'both',
  },
};

// Deprecated in lieu of Design System 2019
const fonts = {
  heading: '"Didot Lt Pro Roman", Didot, times, serif',
  headingWeight: 'normal',
  body: '"proxima nova","helvetica neue",Helvetica,Arial,sans-serif',
  bodyWeight: 'normal',
  light: '"proxima nova light","helvetica neue",Helvetica,Arial,sans-serif',
  lightWeight: 300,
  semiBold: 600,
  bold: '"proxima nova bold","helvetica neue",Helvetica,Arial,sans-serif',
  boldWeight: 'bold',
};

const colors = {
  white: '#fff',
  black: '#000',
  darkGrey60: '#b5b5b5',
  darkGrey: '#4d4d4d',
  lightGrey: '#bbb',
  border: '#d2d2d2',
  sale: '#ef423c',
  urgency: '#d12229',
  text: '#222',
  lightText: '#afafaf',
  link: '#232323',
  secondary: '#42abc8',
  secondary20: '#38889f',
  contrastBg: '#f5f5f5',
  activeDropdown: '#d9eef4',
  borderDropdown: '#dfdfdf',
  transparent: 'transparent',

  // Design System 2019

  // Primary
  brandBlack: '#232323',
  brand01: '#42abc8',
  brandAccept: '#349c5f',
  brandWarn: '#ff8e4d',
  brandError: '#ef423c',
  ui02: '#696969',
  ui03: '#efefef',
  ui04: '#fff',
  txtPrimary: '#464646',
  txtSecondary: '#b0b0b0',
  dividingLine: '#a8a8a8',
  lightCaptionHeading: '#979797',

  textDarkGrey: '#4a4a4a',
  linkHover: '#696969',
  linkHoverWhite: '#dfdfdf',
  inactiveBtn: '#8c8c8c',
  blue50: '#ecf7fa',
  gray200: '#e8e8e8',

  whiteOpacity0: 'rgba(255, 255, 255, 0)',
  whiteOpacity80: 'rgba(255, 255, 255, 0.8)',
  whiteOpacity22: 'rgba(255, 255, 255, 0.22)',
  whiteOpacity25: 'rgba(255, 255, 255, 0.25)',
  whiteOpacity37: 'rgba(255,255,255,0.37)',

  blackOpacity80: 'rgba(0, 0, 0, 0.8)',
  blackOpacity30: 'rgba(0, 0, 0, 0.3)',
  blackOpacity10: 'rgba(0, 0, 0, 0.1)',
  blackOpacity70: 'rgba(0, 0, 0, 0.7)',
  blackOpacity0: 'rgba(0, 0, 0, 0)',

  darkOpacity0: 'rgba(35, 35, 35, 0)',
  darkOpacity25: 'rgba(35, 35, 35, 0.25)',
  darkOpacity37: 'rgba(35, 35, 35, 0.37)',
};

export const sliceColourTheme = {
  white: {
    backgroundColour: 'transparent',
    textColour: colors.brandBlack,
    linkHover: colors.linkHover,
    buttonHover: {
      backgroundColour: colors.brandBlack,
      textColour: colors.white,
    },
  },
  grey: {
    backgroundColour: colors.contrastBg,
    textColour: colors.brandBlack,
    linkHover: colors.linkHover,
    buttonHover: {
      backgroundColour: colors.brandBlack,
      textColour: colors.white,
    },
  },
  black: {
    backgroundColour: colors.brandBlack,
    textColour: colors.white,
    linkHover: colors.linkHoverWhite,
    buttonHover: {
      backgroundColour: colors.white,
      textColour: colors.brandBlack,
    },
  },
};

export const sliceFontStyle = {
  'Heading (Didot)': fonts.heading,
  'Bold (Proxima Nova Bold)': fonts.bold,
  'Body (Proxima Nova Regular)': fonts.body,
};

/* Design System 2019
 *
 * As part of the emerging style guide the following structure
 * has been created and is set to be observed moving forward.
 * I've put a deprecation notice on the originals and will leave
 * for now so as to provide backwards compatibility.
 */

// 1. Typography - see ./typography.js

export const getFontFamilyAndWeightFor = theme => ({
  fontFamily: fonts[theme],
  fontWeight: fonts[`${theme}Weight`],
});

const spacing = {
  vertical: {
    xxs: 16,
    xs: 24,
    sm: 32,
    md: 48,
    lg: 56,
    xl: 64,
  },
};

const padding = {
  horizontal: 20,
  gutter: 16,
};

export const em = (fontSizes, px, bp = 'sm') => `${round(px / fontSizes[bp])}em`;

export const buttonReset = extraStyles => ({
  // override page styles for buttons
  '&:not(.container-button)': {
    border: 0,
    borderRadius: 0,
    minHeight: 0,
    padding: 0,
    color: colors.brandBlack,
    '&,&:hover,&:focus': {
      background: 'transparent none',
    },
    ...extraStyles,
  },
});

export const titleCommon = {
  fontFamily: fonts.bold,
  fontWeight: 400, // FF & Safari will try to make this already bold text bolder
  fontSize: 24,
  letterSpacing: 0.3,
  lineHeight: '36px',
  margin: 0,
  color: colors.brandBlack,
};

export const largeTitle = {
  ...titleCommon,
};

export const cardTitle = {
  ...titleCommon,
  fontSize: 18,
  lineHeight: '28px',
  marginBottom: 4,
  color: colors.brandBlack,
};

export const cardCopy = {
  fontSize: 16,
  fontFamily: fonts.body,
  lineHeight: 1.5,
  color: colors.txtPrimary,
  marginBottom: padding.gutter,
  [breakpoints.up('md')]: {
    maxWidth: '75%',
  },
};

export const cardOverlayTitle = {
  ...titleCommon,
  fontSize: 20,
  color: colors.brandBlack,
};

// Using scale() on images can cause janking at the beginning and end of the transition, usually
// only on lower resolution screens. In Chrome it occurs in about half of all instances, even on
// 2.0 device-pixel-ratio displays, apparently depending on sub-pixel rounding. Using a not-quite
// 1.0 scale as a starting point avoids that. (1 + Number.EPSILON) does work but not on IE11; five
// decimal places seems to be the most that browser will accept before it rounds it off.
// This hack makes the zoom appear smooth on all tested browsers and screens except for IE11 on 1.0
// device-pixel-ratio displays.
const originalScale = 'scale(1.00002)';

const imageZoom = (targetSelector, imageSelector) => ({
  [`& ${targetSelector}`]: {
    [imageSelector]: {
      transition: 'all 0.45s ease-out',
      transform: originalScale,
    },
  },
  '&:focus, &:hover': {
    [`& ${targetSelector}`]: {
      [imageSelector]: {
        // note: scale(1.01) triggers cRaZy transitions on IE11 and Edge but 1.011 doesn’t
        transform: 'scale(1.011)',
      },
    },
  },
  '&:active': {
    '&, &:focus, &:hover': {
      [`& ${targetSelector}`]: {
        [imageSelector]: {
          transform: originalScale,
        },
      },
    },
  },
});

export const cardHover = (targetSelector, imageSelector = '& > *') => ({
  tapHighlightColor: 'rgba(0,0,0,0)',
  [`& ${targetSelector}`]: {
    transition: 'box-shadow 0.3s ease-in-out',
  },
  extend: [
    {
      '&:focus, &:hover': {
        [`& ${targetSelector}`]: {
          boxShadow: '0 1px 4px 0 rgba(0,0,0,.16), 0 4px 8px 0 rgba(0,0,0,.24)',
        },
      },
      '&:active': {
        '&, &:focus, &:hover': { // :focus and :hover normally win, but we want :active to win
          [`& ${targetSelector}`]: {
            boxShadow: '0 1px 4px 0 rgba(0,0,0,.16), 0 1px 4px 0 rgba(0,0,0,.16)',
          },
        },
      },
    },
    ...(imageSelector && !disableImageZoom
      ? [imageZoom(targetSelector, imageSelector)]
      : []
    ),
  ],
});

export const animatedUnderlineHover = {
  position: 'relative',
  '&::after': {
    content: '""',
    display: 'block',
    position: 'absolute',
    left: 0,
    right: '0.3em',
    bottom: 0,
    borderBottom: '2px solid',
    opacity: 0.8,
    transform: 'scaleX(0)',
    transformOrigin: '100% 50%',
    transition: 'transform 0.15s ease-out',
    pointerEvents: 'none',
  },
  '&:focus, &:hover': {
    '&::after': {
      transform: 'scaleX(1)',
      transformOrigin: '0 50%',
    },
  },
};

const expandSettings = (settings, settingsKey) => settings.reduce(
  (properties, [property, settingPropName, resolver]) => ({
    ...properties,
    // eslint-disable-next-line react/destructuring-assignment
    [property]: props => resolver(props[settingPropName][settingsKey], props),
  }),
  {},
);
export const makeResponsiveSettings = (...settings) => ({
  ...expandSettings(settings, 'mobile'),
  [breakpoints.up('md')]: {
    ...expandSettings(settings, 'desktop'),
  },
});

export const container = {
  margin: [0, 'auto'],
  padding: [0, padding.horizontal],
  maxWidth: breakpoints.width('xl') + padding.horizontal * 2,
};

export const easeOutQuad = 'cubic-bezier(0.250, 0.460, 0.450, 0.940)';
export const easeOutCubic = 'cubic-bezier(0.215, 0.610, 0.355, 1.000)';
export const easeOutQuint = 'cubic-bezier(0.230, 1.000, 0.320, 1.000)';
export const easeOutExpo = 'cubic-bezier(0.190, 1.000, 0.220, 1.000)';

export const getBackgroundImageWidth = backgroundImageWidth => (backgroundImageWidth === 'Page width' ? 1280 : 'none');
export const getBackgroundColour = (backgroundColour, backgroundImage) => (backgroundImage ? 'transparent' : backgroundColour);

export default {
  breakpoints,
  fonts,
  colors,
  padding,
  spacing,
  typography,
};
